import { useMutation, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plug, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { api, ensureOk, unwrap } from '@/api/client';
import { mensajeDe } from '@/api/errors';
import type { components } from '@/api/schema';
import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { useStepUp } from '@/auth/StepUp';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { Cargando, Encabezado, ErrorCarga } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import {
  PROVEEDORES_IA,
  useEstadisticasIa,
  useProveedores,
  useTareasIa,
  type Proveedor,
  type TareaIa,
} from '@/features/ia/queries';

type Alta = components['schemas']['AiProviderCreateBody'];
type Prueba = components['schemas']['AiProviderTestResult'];

function FormAlta({ onListo }: { onListo: () => void }) {
  const qc = useQueryClient();
  const { proteger, dialogo } = useStepUp();
  const [f, setF] = useState<Alta>({ kind: 'anthropic', label: '', apiKey: '' });
  const crear = useMutation({
    mutationFn: () =>
      proteger(() =>
        unwrap(
          api.POST('/api/v1/admin/ai/providers', { body: { ...f, baseUrl: f.baseUrl || null } }),
        ),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ia', 'proveedores'] });
      toast.success('Proveedor registrado. La llave quedó cifrada.');
      onListo();
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        crear.mutate();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Campo etiqueta="Proveedor">
          <select
            className="input-vidrio"
            value={f.kind}
            onChange={(e) => setF({ ...f, kind: e.target.value as Alta['kind'] })}
          >
            {Object.entries(PROVEEDORES_IA).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </Campo>
        <Campo etiqueta="Nombre para identificarlo">
          <input
            className="input-vidrio"
            required
            minLength={2}
            maxLength={60}
            value={f.label}
            placeholder="Claude (cuenta Consalud)"
            onChange={(e) => setF({ ...f, label: e.target.value })}
          />
        </Campo>
      </div>
      <Campo
        etiqueta="Llave de API"
        ayuda="Se guarda cifrada en el servidor; después solo se muestran sus últimos 4 caracteres."
      >
        <input
          className="input-vidrio font-mono"
          type="password"
          autoComplete="off"
          required
          minLength={8}
          value={f.apiKey}
          onChange={(e) => setF({ ...f, apiKey: e.target.value })}
        />
      </Campo>
      {(f.kind === 'openai_compatible' || f.kind === 'openai') && (
        <Campo
          etiqueta={f.kind === 'openai' ? 'URL base (opcional)' : 'URL base'}
          ayuda="Ej. https://openrouter.ai/api/v1"
        >
          <input
            className="input-vidrio"
            type="url"
            required={f.kind === 'openai_compatible'}
            value={f.baseUrl ?? ''}
            onChange={(e) => setF({ ...f, baseUrl: e.target.value })}
          />
        </Campo>
      )}
      <div className="flex justify-end">
        <button type="submit" className="btn-cs btn-primario" disabled={crear.isPending}>
          Guardar
        </button>
      </div>
      {dialogo}
    </form>
  );
}

function FilaProveedor({ p }: { p: Proveedor }) {
  const qc = useQueryClient();
  const editable = useCan('configuracion', 'editar');
  const { proteger, dialogo } = useStepUp();
  const [modelo, setModelo] = useState(p.modelSuggestions[0] ?? '');
  const [prueba, setPrueba] = useState<Prueba | null>(null);
  const [llave, setLlave] = useState<string | null>(null);
  const [borrar, setBorrar] = useState(false);
  const ruta = { params: { path: { id: String(p.id) } } };
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['ia'] });
  const probar = useMutation({
    mutationFn: () =>
      unwrap<Prueba>(
        api.POST('/api/v1/admin/ai/providers/{id}/test', { ...ruta, body: { model: modelo } }),
      ),
    onSuccess: setPrueba,
    onError: (e) => toast.error(mensajeDe(e)),
  });
  const actualizar = useMutation({
    mutationFn: (body: { isEnabled?: boolean; apiKey?: string }) =>
      proteger(() => unwrap(api.PATCH('/api/v1/admin/ai/providers/{id}', { ...ruta, body }))),
    onSuccess: () => {
      refrescar();
      setLlave(null);
      toast.success('Proveedor actualizado.');
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  const eliminar = useMutation({
    mutationFn: () => proteger(() => ensureOk(api.DELETE('/api/v1/admin/ai/providers/{id}', ruta))),
    onSuccess: () => {
      refrescar();
      setBorrar(false);
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <li className="card-vidrio space-y-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-extrabold">
            {p.label} {!p.isEnabled && <span className="insignia ml-1">Desactivado</span>}
          </p>
          <p className="text-xs text-texto-3">
            {PROVEEDORES_IA[p.kind]} · llave …{p.keyLast4}
            {p.baseUrl && ` · ${p.baseUrl}`}
          </p>
        </div>
        {editable && (
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-cs btn-contorno px-2 py-1 text-xs"
              onClick={() => actualizar.mutate({ isEnabled: !p.isEnabled })}
            >
              {p.isEnabled ? 'Desactivar' : 'Activar'}
            </button>
            <button
              type="button"
              className="btn-cs btn-contorno px-2 py-1 text-xs"
              onClick={() => setLlave('')}
            >
              <KeyRound size={14} aria-hidden /> Cambiar llave
            </button>
            <button
              type="button"
              className="rounded-full p-1.5 hover:bg-[var(--pista)]"
              aria-label="Eliminar"
              onClick={() => setBorrar(true)}
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}
      </div>
      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          <input
            className="input-vidrio max-w-xs"
            list={`modelos-${p.id}`}
            value={modelo}
            placeholder="Modelo a probar"
            onChange={(e) => setModelo(e.target.value)}
            aria-label="Modelo"
          />
          <datalist id={`modelos-${p.id}`}>
            {p.modelSuggestions.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
          <button
            type="button"
            className="btn-cs btn-contorno"
            disabled={probar.isPending || modelo.length < 2}
            onClick={() => probar.mutate()}
          >
            <Plug size={16} aria-hidden /> {probar.isPending ? 'Probando…' : 'Probar conexión'}
          </button>
          {prueba && (
            <span
              className={`text-sm font-semibold ${prueba.ok ? 'text-verde-texto' : 'text-rojo-texto'}`}
            >
              {prueba.message} ({prueba.durationMs} ms)
            </span>
          )}
        </div>
      )}
      <Modal
        abierto={llave !== null}
        onCerrar={() => setLlave(null)}
        titulo={`Nueva llave para ${p.label}`}
      >
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (llave) actualizar.mutate({ apiKey: llave });
          }}
        >
          <input
            className="input-vidrio font-mono"
            type="password"
            autoComplete="off"
            minLength={8}
            required
            value={llave ?? ''}
            onChange={(e) => setLlave(e.target.value)}
            aria-label="Llave"
          />
          <div className="flex justify-end">
            <button type="submit" className="btn-cs btn-primario" disabled={actualizar.isPending}>
              Guardar llave
            </button>
          </div>
        </form>
      </Modal>
      <ConfirmarAccion
        abierto={borrar}
        onCerrar={() => setBorrar(false)}
        titulo={`Eliminar ${p.label}`}
        descripcion="Las tareas que lo usan quedarán sin proveedor."
        textoBoton="Eliminar"
        peligro
        ocupado={eliminar.isPending}
        onConfirmar={() => eliminar.mutate()}
      />
      {dialogo}
    </li>
  );
}

function FilaTarea({ t, proveedores }: { t: TareaIa; proveedores: Proveedor[] }) {
  const qc = useQueryClient();
  const editable = useCan('configuracion', 'editar');
  const [f, setF] = useState({
    providerId: t.providerId ?? 0,
    model: t.model ?? '',
    temperature: t.temperature ?? null,
    maxTokens: t.maxTokens,
  });
  const prov = proveedores.find((p) => p.id === f.providerId);
  const guardar = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT('/api/v1/admin/ai/tasks/{task}', { params: { path: { task: t.task } }, body: f }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ia', 'tareas'] });
      toast.success(`«${t.name}» configurada.`);
    },
    onError: (e) => toast.error(mensajeDe(e)),
  });
  return (
    <tr>
      <td className="max-w-xs">
        <p className="font-bold">{t.name}</p>
        <p className="text-xs text-texto-3">{t.description}</p>
      </td>
      <td>
        <select
          className="input-vidrio"
          disabled={!editable}
          value={f.providerId || ''}
          onChange={(e) => setF({ ...f, providerId: Number(e.target.value) })}
          aria-label="Proveedor"
        >
          <option value="">Sin configurar</option>
          {proveedores
            .filter((p) => p.isEnabled)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
        </select>
      </td>
      <td>
        <input
          className="input-vidrio"
          list={`m-${t.task}`}
          disabled={!editable}
          value={f.model}
          placeholder="Modelo"
          onChange={(e) => setF({ ...f, model: e.target.value })}
          aria-label="Modelo"
        />
        <datalist id={`m-${t.task}`}>
          {prov?.modelSuggestions.map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
      </td>
      <td>
        <input
          className="input-vidrio w-24"
          inputMode="decimal"
          disabled={!editable || prov?.kind === 'anthropic'}
          placeholder={prov?.kind === 'anthropic' ? 'n/a' : 'auto'}
          value={f.temperature ?? ''}
          onChange={(e) =>
            setF({ ...f, temperature: e.target.value === '' ? null : Number(e.target.value) })
          }
          aria-label="Temperatura"
        />
      </td>
      <td>
        <input
          type="number"
          className="input-vidrio w-28"
          disabled={!editable}
          min={500}
          max={64000}
          value={f.maxTokens}
          onChange={(e) => setF({ ...f, maxTokens: Number(e.target.value) })}
          aria-label="Tokens máximos"
        />
      </td>
      <td>
        {editable && (
          <button
            type="button"
            className="btn-cs btn-primario px-3 py-1 text-xs"
            disabled={!f.providerId || f.model.length < 2 || guardar.isPending}
            onClick={() => guardar.mutate()}
          >
            Guardar
          </button>
        )}
      </td>
    </tr>
  );
}

function ProveedoresIa() {
  const prov = useProveedores();
  const tareas = useTareasIa();
  const stats = useEstadisticasIa();
  const editable = useCan('configuracion', 'editar');
  const [alta, setAlta] = useState(false);
  return (
    <>
      <Encabezado
        titulo="Proveedores de IA"
        descripcion="Registra las llaves de cada proveedor y elige qué modelo hace cada tarea."
        acciones={
          editable && (
            <button type="button" className="btn-cs btn-primario" onClick={() => setAlta(true)}>
              <Plus size={16} aria-hidden /> Agregar proveedor
            </button>
          )
        }
      />
      {prov.isPending && <Cargando />}
      {prov.isError && <ErrorCarga error={prov.error} onReintentar={() => void prov.refetch()} />}
      <ul className="mb-8 grid gap-4 lg:grid-cols-2">
        {prov.data?.map((p) => (
          <FilaProveedor key={p.id} p={p} />
        ))}
      </ul>
      <section className="panel-vidrio mb-8 overflow-x-auto">
        <h2 className="mb-3 text-lg font-extrabold">Modelo por tarea</h2>
        {tareas.data && prov.data && (
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>Tarea</th>
                <th>Proveedor</th>
                <th>Modelo</th>
                <th>Temperatura</th>
                <th>Tokens máx.</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tareas.data.map((t) => (
                <FilaTarea
                  key={`${t.task}-${t.providerId}-${t.model}`}
                  t={t}
                  proveedores={prov.data}
                />
              ))}
            </tbody>
          </table>
        )}
      </section>
      {stats.data && stats.data.last30Days.length > 0 && (
        <section className="panel-vidrio overflow-x-auto">
          <h2 className="mb-3 text-lg font-extrabold">Uso de los últimos 30 días</h2>
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>Proveedor</th>
                <th>Modelo</th>
                <th>Llamadas</th>
                <th>Exitosas</th>
                <th>Tokens entrada</th>
                <th>Tokens salida</th>
                <th>Tiempo prom.</th>
              </tr>
            </thead>
            <tbody>
              {stats.data.last30Days.map((u) => (
                <tr key={u.provider + u.model}>
                  <td>{PROVEEDORES_IA[u.provider] ?? u.provider}</td>
                  <td className="font-mono text-xs">{u.model}</td>
                  <td className="tabular">{u.calls}</td>
                  <td className="tabular">{u.ok}</td>
                  <td className="tabular">{u.inputTokens.toLocaleString('es-MX')}</td>
                  <td className="tabular">{u.outputTokens.toLocaleString('es-MX')}</td>
                  <td className="tabular">{(u.avgMs / 1000).toFixed(1)} s</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      <Modal abierto={alta} onCerrar={() => setAlta(false)} titulo="Agregar proveedor de IA">
        <FormAlta onListo={() => setAlta(false)} />
      </Modal>
    </>
  );
}

export function ProveedoresIaPage() {
  return (
    <PermissionGate seccion="configuracion">
      <ProveedoresIa />
    </PermissionGate>
  );
}
